import type { MedplumClient } from "@medplum/core";
import { stableSerialize } from "@/lib/hooks/use-model-subscription";

/**
 * Refcounted tally of the distinct Medplum WebSocket subscriptions a client currently holds open.
 * `size` is exactly what counts against Medplum's per-client cap: Medplum keys a server-side
 * Subscription on `(criteria, subscriptionProps)`, so callers add/remove by that composite key while
 * `criteria()` reports the human-readable criteria for display.
 *
 * Pure and side-effect-free — the instrumentation wrapper below owns key derivation and globals.
 */
export class SubscriptionCounter {
  private readonly entries = new Map<string, { label: string; refCount: number }>();

  get size(): number {
    return this.entries.size;
  }

  /** Display criteria for every open subscription (one entry per distinct key). */
  criteria(): string[] {
    return [...this.entries.values()].map((entry) => entry.label);
  }

  add(key: string, label: string): void {
    const existing = this.entries.get(key);
    if (existing) {
      existing.refCount += 1;
      return;
    }
    this.entries.set(key, { label, refCount: 1 });
  }

  remove(key: string): void {
    const existing = this.entries.get(key);
    if (!existing) {
      return;
    }
    existing.refCount -= 1;
    if (existing.refCount <= 0) {
      this.entries.delete(key);
    }
  }
}

/** Warn in the console once the open-subscription count crosses this, so cap pressure is visible. */
const WARN_THRESHOLD = 15;

interface SubscriptionDebugWindow {
  __medplumSubscriptions?: () => { count: number; criteria: string[] };
}

/**
 * Compose the cap-relevant identity of a subscription. Medplum treats `create`-only and
 * `update`-only subscriptions for the same criteria as separate server-side Subscriptions, so the
 * key must fold in `subscriptionProps` — otherwise the tally under-counts exactly those pairs.
 */
function subscriptionKey(criteria: string, subscriptionProps: unknown): string {
  return subscriptionProps === undefined
    ? criteria
    : `${criteria} ${stableSerialize(subscriptionProps)}`;
}

/**
 * Dev-only: wrap a browser Medplum client so every `subscribeToCriteria` /
 * `unsubscribeFromCriteria` updates a live tally. Exposes `window.__medplumSubscriptions()` for
 * ad-hoc inspection and warns when the open count crosses {@link WARN_THRESHOLD}.
 *
 * No-op in production and when there is no `window` (SSR). Instrumentation only — never gate product
 * behavior on it.
 */
export function instrumentSubscriptionDebug(medplum: MedplumClient): void {
  if (process.env.NODE_ENV === "production" || typeof window === "undefined") {
    return;
  }

  type SubscribeFn = MedplumClient["subscribeToCriteria"];
  type UnsubscribeFn = MedplumClient["unsubscribeFromCriteria"];

  const counter = new SubscriptionCounter();
  const originalSubscribe: SubscribeFn = medplum.subscribeToCriteria.bind(medplum);
  const originalUnsubscribe: UnsubscribeFn = medplum.unsubscribeFromCriteria.bind(medplum);

  medplum.subscribeToCriteria = ((...args: Parameters<SubscribeFn>) => {
    // Count only after a successful subscribe so a throw doesn't leave a phantom entry.
    const result = originalSubscribe(...args);
    counter.add(subscriptionKey(args[0], args[1]), args[0]);
    if (counter.size > WARN_THRESHOLD) {
      console.warn(`[medplum-subs] ${counter.size} open subscriptions`, counter.criteria());
    }
    return result;
  }) as SubscribeFn;

  medplum.unsubscribeFromCriteria = ((...args: Parameters<UnsubscribeFn>) => {
    counter.remove(subscriptionKey(args[0], args[1]));
    return originalUnsubscribe(...args);
  }) as UnsubscribeFn;

  (window as SubscriptionDebugWindow).__medplumSubscriptions = () => ({
    count: counter.size,
    criteria: counter.criteria(),
  });
}
